import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { CS } from "@/constants/testIds/clickshield";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(isLogin ? "demo@clickshield.ai" : "");
  const [password, setPassword] = useState(isLogin ? "Demo1234!" : "");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(email, password, name);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#040914] bg-tech-grid flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute -top-32 right-0 w-[30rem] h-[30rem] rounded-full bg-cyan-neon/10 blur-[130px] pointer-events-none" />
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm">
        <ArrowLeft className="w-4 h-4" /> Back home
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="flex items-center gap-2.5 justify-center mb-8">
          <ShieldCheck className="w-8 h-8 text-cyan-neon" />
          <span className="font-display text-xl text-white">ClickShield<span className="text-cyan-neon"> AI</span></span>
        </div>

        <div className="rounded-2xl neon-border bg-[#0B101A]/80 backdrop-blur-xl p-8">
          <h1 className="font-display text-2xl text-white mb-1.5">
            {isLogin ? "Welcome back" : "Start your free trial"}
          </h1>
          <p className="text-sm text-slate-400 mb-7">
            {isLogin ? "Log in to your fraud dashboard." : "14 days free · no credit card required."}
          </p>

          {!isLogin && localStorage.getItem("cs_ref") && (
            <p data-testid="auth-referral-badge" className="-mt-4 mb-6 text-xs text-cyan-neon bg-cyan-neon/10 border border-cyan-neon/25 rounded-lg px-3 py-2">
              🎁 Referral invite applied — your friend earns a free month when you upgrade.
            </p>
          )}

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="font-mono text-[11px] tracking-widest uppercase text-slate-500">Name</label>
                <input
                  data-testid={CS.authName}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Jane Advertiser"
                  className="mt-2 w-full px-4 py-3 rounded-lg bg-[#040914] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-neon/50 focus:outline-none transition-colors"
                />
              </div>
            )}
            <div>
              <label className="font-mono text-[11px] tracking-widest uppercase text-slate-500">Email</label>
              <input
                data-testid={CS.authEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="mt-2 w-full px-4 py-3 rounded-lg bg-[#040914] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-neon/50 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="font-mono text-[11px] tracking-widest uppercase text-slate-500">Password</label>
                {isLogin && (
                  <Link data-testid={CS.authForgotLink} to="/forgot-password" className="text-xs text-cyan-neon hover:underline">
                    Forgot password?
                  </Link>
                )}
              </div>
              <input
                data-testid={CS.authPassword}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-2 w-full px-4 py-3 rounded-lg bg-[#040914] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-neon/50 focus:outline-none transition-colors"
              />
            </div>

            {error && (
              <p data-testid={CS.authError} className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              data-testid={CS.authSubmit}
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-full bg-cyan-neon text-[#040914] font-semibold hover:glow-cyan transition-shadow inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isLogin ? "Log in" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            {isLogin ? "New to ClickShield? " : "Already have an account? "}
            <Link data-testid={CS.authSwitch} to={isLogin ? "/signup" : "/login"} className="text-cyan-neon hover:underline">
              {isLogin ? "Start free trial" : "Log in"}
            </Link>
          </p>
        </div>

        {isLogin && (
          <p className="mt-4 text-center font-mono text-[11px] text-slate-600">
            Demo: demo@clickshield.ai / Demo1234!
          </p>
        )}
      </motion.div>
    </div>
  );
}
